import { generateGraphHtml } from "./generateGraphHtml";
import {
  APIComponent,
  ComponentConfiguration,
  ComponentStyle,
  FieldDefinition,
  FieldUpdateOptions,
  NormalizationResult,
  NormalizedComponent,
  RenderOptions,
} from "./types";

// utils/componentNormalizer.ts
/**
 * ComponentNormalizer
 *
 * Converts API components into normalized internal structure.
 * Handles both array and object field formats.
 */

export class ComponentNormalizer {
  /**
   * Normalize a single component
   */
  normalize(
    apiComponent: APIComponent,
    frontendId?: string,
    position?: { x: number; y: number }
  ): NormalizedComponent {
    try {
      // Extract and normalize fields
      const fields = this.extractFields(apiComponent);

      // Create normalized component
      const normalized: NormalizedComponent = {
        id:
          frontendId ||
          `component-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        componentId: apiComponent.id,
        type: apiComponent.componentName,
        alias: apiComponent.componentAlias,

        // Layout
        x: position?.x || 50,
        y: position?.y || 50,
        width: apiComponent.defaultSize?.width || 300,
        height: apiComponent.defaultSize?.height || 150,
        zIndex: 1,
        rotation: 0,

        // State
        isVisible: true,
        isLocked: false,

        // Content - Store original HTML
        originalHtml: apiComponent.htmlData,
        templateHtml: apiComponent.htmlData,

        // Configuration
        fields: fields,
        style: this.getDefaultStyle(),
        graphConfig:
          (apiComponent as any).configuration?.graphConfig ||
          (apiComponent as any).graphConfig,

        // Metadata
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
        },
      };

      console.log(`✅ Normalized: ${apiComponent.componentName}`, {
        fieldCount: fields.length,
        alias: apiComponent.componentAlias,
      });

      return normalized;
    } catch (error: any) {
      console.error(`❌ Error normalizing component:`, error);
      throw error;
    }
  }

  /**
   * Normalize multiple components with error handling
   */
  normalizeMany(
    apiComponents: APIComponent[],
    existingLayout?: any
  ): NormalizationResult {
    const components: NormalizedComponent[] = [];
    const errors: Array<{ componentId: string; error: string }> = [];
    const warnings: Array<{ componentId: string; warning: string }> = [];

    apiComponents.forEach((apiComponent, index) => {
      try {
        let position = { x: 50, y: 50 + index * 20 };
        let frontendId: string | undefined;

        // Get position from existing layout if available
        if (existingLayout) {
          const layoutEntry = Object.entries(existingLayout).find(
            ([_, layout]: [string, any]) =>
              layout.componentId === apiComponent.id
          );

          if (layoutEntry) {
            const [id, layout] = layoutEntry as [string, any];
            frontendId = id;
            position = { x: layout.x, y: layout.y };
          }
        }

        const normalized = this.normalize(apiComponent, frontendId, position);
        components.push(normalized);

        // Warn if no fields
        if (normalized.fields.length === 0) {
          warnings.push({
            componentId: normalized.id,
            warning: "No fields found in component",
          });
        }
      } catch (error: any) {
        errors.push({
          componentId: `${apiComponent.id}`,
          error: error.message || "Unknown error",
        });
      }
    });

    return { components, errors, warnings };
  }

  /**
   * Extract fields from API component
   * Handles both array and object formats, prioritizing camelCase
   */
  private extractFields(apiComponent: APIComponent): FieldDefinition[] {
    const fields: FieldDefinition[] = [];

    // Check common locations for fields in API response (prioritize camelCase)
    const possibleFieldSources = [
      (apiComponent as any).configuration?.availableFields,
      (apiComponent as any).configuration?.available_fields,
      (apiComponent as any).availableFields,
      (apiComponent as any).available_fields,
      apiComponent.fields,
      (apiComponent as any).config?.fields,
      (apiComponent as any).meta?.availableFields,
      (apiComponent as any).meta?.available_fields,
    ];

    for (const source of possibleFieldSources) {
      if (source && Array.isArray(source)) {
        return source.map((field: any, index: number) =>
          this.normalizeField(field, index)
        );
      }

      if (source && typeof source === "object" && !Array.isArray(source)) {
        return Object.entries(source).map(
          ([fieldName, fieldData]: [string, any], index: number) =>
            this.normalizeField({ ...fieldData, fieldName }, index)
        );
      }
    }

    return fields;
  }

  /**
   * Normalize a single field
   * Maps various API naming conventions (field_key, fieldKey, etc.) to the internal fieldName
   */
  public normalizeField(field: any, index: number): FieldDefinition {
    // Priority: camelCase -> snake_case -> fallback
    const fieldName =
      field.fieldKey ||
      field.field_key ||
      field.fieldName ||
      field.field_name ||
      `field_${index}`;
    const label =
      field.fieldLabel ||
      field.field_label ||
      field.label ||
      fieldName ||
      `Field ${index}`;

    return {
      // Identity
      fieldName: fieldName,
      label: label,
      originalLabel: label,

      // Visibility Controls
      isActive: field.isActive !== false,
      showLabel: field.showLabel !== false,
      showValue: field.showValue !== false,

      // Data Mapping
      parameter: field.dataSource || field.data_source || field.parameter || "",
      placeholder: field.placeholder || `{{${fieldName}}}`,
      labelPlaceholder: field.labelPlaceholder || `{{${fieldName}_label}}`,

      // Metadata
      fieldType: field.fieldType || field.field_type || "text",
      isRequired: field.isRequired || field.is_required || false,
      order:
        field.defaultOrder !== undefined
          ? field.defaultOrder
          : field.default_order !== undefined
          ? field.default_order
          : field.order !== undefined
          ? field.order
          : index,
      defaultValue: field.defaultValue,

      // Validation
      validation: field.validation,

      // HTML Elements (legacy/fallback)
      htmlElements: field.htmlElements || {},
    };
  }

  /**
   * Get default component style
   */
  private getDefaultStyle(): ComponentStyle {
    return {
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderWidth: 1,
      borderStyle: "solid",
      borderRadius: 0,
      padding: 10,
      fontSize: 14,
      color: "#374151",
      textAlign: "left",
    };
  }
}

// Export singleton
export const componentNormalizer = new ComponentNormalizer();

// utils/configurationManager.ts
/**
 * ConfigurationManager
 *
 * Centralized manager for component configurations.
 * Handles field updates, reordering, and validation.
 */

/**
 * Configuration Manager
 *
 * Manages field configurations for all components.
 * Handles field visibility, labels, ordering, and all configuration operations.
 */
export class ConfigurationManager {
  private config: ComponentConfiguration = {};

  /**
   * Initialize configuration from normalized components
   */
  initialize(components: NormalizedComponent[]): void {
    this.config = {};
    console.log("initializing configuration for ", components);

    components.forEach((component) => {
      this.config[component.id] = {
        style: { ...component.style },
        fields: component.fields.map((field) => ({ ...field })),
        content: component.content,
        imageUrl: component.imageUrl,
        metadata: {
          lastUpdated: new Date().toISOString(),
          version: 1,
        },
      };
    });

    console.log(
      `✅ Configuration initialized for ${components.length} components`,
      this.config
    );
  }

  /**
   * Clear all configuration
   * Used when resetting or re-initializing the template system
   */
  clear(): void {
    this.config = {};
    console.log("✅ Configuration cleared");
  }

  /**
   * Get configuration for a component
   */
  getConfig(componentId: string): ComponentConfiguration[string] | null {
    return this.config[componentId] || null;
  }

  /**
   * Get all configurations
   */
  getAllConfigs(): ComponentConfiguration {
    return { ...this.config };
  }

  /**
   * Get fields for a component
   */
  getFields(componentId: string): FieldDefinition[] {
    console.log("getting field for " + componentId, this.config);

    const config = this.config[componentId];
    if (!config) {
      console.warn(`⚠️ No config found for component: ${componentId}`);
      return [];
    }

    // Return fields sorted by order
    return [...config.fields].sort((a, b) => a.order - b.order);
  }

  /**
   * Update a field property
   */
  updateField(
    componentId: string,
    fieldName: string,
    updates: FieldUpdateOptions
  ): void {
    if (!this.config[componentId]) {
      throw new Error(`Component "${componentId}" not found in configuration`);
    }

    const fieldIndex = this.config[componentId].fields.findIndex(
      (f) => f.fieldName === fieldName
    );

    if (fieldIndex === -1) {
      throw new Error(
        `Field "${fieldName}" not found in component "${componentId}"`
      );
    }

    // Apply updates
    const currentField = this.config[componentId].fields[fieldIndex];
    this.config[componentId].fields[fieldIndex] = {
      ...currentField,
      ...updates,
    };

    // Update metadata
    this.config[componentId].metadata.lastUpdated = new Date().toISOString();
    this.config[componentId].metadata.version += 1;

    console.log(`✅ Updated field "${fieldName}"`, updates);
  }

  /**
   * Toggle field active state
   */
  toggleFieldActive(componentId: string, fieldName: string): void {
    const field = this.getField(componentId, fieldName);
    if (!field) {
      throw new Error(`Field "${fieldName}" not found`);
    }

    this.updateField(componentId, fieldName, {
      isActive: !field.isActive,
    });
  }

  /**
   * Toggle label visibility
   */
  toggleLabelVisibility(componentId: string, fieldName: string): void {
    const field = this.getField(componentId, fieldName);
    if (!field) {
      throw new Error(`Field "${fieldName}" not found`);
    }

    this.updateField(componentId, fieldName, {
      showLabel: !field.showLabel,
    });
  }

  /**
   * Toggle value visibility
   */
  toggleValueVisibility(componentId: string, fieldName: string): void {
    const field = this.getField(componentId, fieldName);
    if (!field) {
      throw new Error(`Field "${fieldName}" not found`);
    }

    this.updateField(componentId, fieldName, {
      showValue: !field.showValue,
    });
  }

  /**
   * Update field label
   */
  updateFieldLabel(
    componentId: string,
    fieldName: string,
    newLabel: string
  ): void {
    this.updateField(componentId, fieldName, {
      label: newLabel,
    });
  }

  /**
   * Reorder field
   */
  reorderField(
    componentId: string,
    fieldName: string,
    direction: "up" | "down"
  ): void {
    if (!this.config[componentId]) {
      throw new Error(`Component "${componentId}" not found`);
    }

    const fields = this.config[componentId].fields;
    const currentIndex = fields.findIndex((f) => f.fieldName === fieldName);

    if (currentIndex === -1) {
      throw new Error(`Field "${fieldName}" not found`);
    }

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    // Check bounds
    if (newIndex < 0 || newIndex >= fields.length) {
      console.warn(
        `⚠️ Cannot move field "${fieldName}" ${direction} - out of bounds`
      );
      return;
    }

    // Swap fields
    [fields[currentIndex], fields[newIndex]] = [
      fields[newIndex],
      fields[currentIndex],
    ];

    // Update order property for all fields
    fields.forEach((field, index) => {
      field.order = index;
    });

    // Update metadata
    this.config[componentId].metadata.lastUpdated = new Date().toISOString();
    this.config[componentId].metadata.version += 1;

    console.log(`✅ Reordered field "${fieldName}" ${direction}`);
  }

  /**
   * Get a specific field
   */
  private getField(
    componentId: string,
    fieldName: string
  ): FieldDefinition | null {
    const config = this.config[componentId];
    if (!config) return null;

    return config.fields.find((f) => f.fieldName === fieldName) || null;
  }

  /**
   * Update custom content for a component
   */
  updateContent(componentId: string, content: string): void {
    if (!this.config[componentId]) return;

    this.config[componentId].content = content;
    this.config[componentId].metadata.lastUpdated = new Date().toISOString();
    this.config[componentId].metadata.version += 1;

    console.log(`✅ Updated content for component: ${componentId}`);
  }

  /**
   * Get custom content for a component
   */
  getContent(componentId: string): string {
    return this.config[componentId]?.content || "";
  }

  /**
   * Update image URL for a component
   */
  updateImageUrl(componentId: string, imageUrl: string): void {
    if (!this.config[componentId]) return;

    this.config[componentId].imageUrl = imageUrl;
    this.config[componentId].metadata.lastUpdated = new Date().toISOString();
    this.config[componentId].metadata.version += 1;

    console.log(`✅ Updated image URL for component: ${componentId}`);
  }

  /**
   * Get image URL for a component
   */
  getImageUrl(componentId: string): string {
    return this.config[componentId]?.imageUrl || "";
  }

  /**
   * Add a new component to configuration
   */
  addComponent(component: NormalizedComponent): void {
    this.config[component.id] = {
      style: { ...component.style },
      fields: component.fields.map((field) => ({ ...field })),
      content: component.content,
      imageUrl: component.imageUrl,
      metadata: {
        lastUpdated: new Date().toISOString(),
        version: 1,
      },
    };

    console.log(`✅ Added component "${component.type}" to configuration`);
  }

  /**
   * Remove a component from configuration
   */
  removeComponent(componentId: string): void {
    if (this.config[componentId]) {
      delete this.config[componentId];
      console.log(`✅ Removed component "${componentId}" from configuration`);
    }
  }

  /**
   * Validate configuration
   */
  validate(): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    Object.entries(this.config).forEach(([componentId, config]) => {
      // Check if fields is an array
      if (!Array.isArray(config.fields)) {
        errors.push(`Component "${componentId}": fields must be an array`);
      }

      // Check for duplicate field names
      const fieldNames = new Set<string>();
      config.fields.forEach((field) => {
        if (fieldNames.has(field.fieldName)) {
          errors.push(
            `Component "${componentId}": duplicate field name "${field.fieldName}"`
          );
        }
        fieldNames.add(field.fieldName);
      });
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Export configuration
   */
  export(): ComponentConfiguration {
    return JSON.parse(JSON.stringify(this.config));
  }

  /**
   * Get configuration statistics
   */
  getStats(): {
    componentCount: number;
    totalFields: number;
    activeFields: number;
    inactiveFields: number;
  } {
    const componentCount = Object.keys(this.config).length;
    let totalFields = 0;
    let activeFields = 0;
    let inactiveFields = 0;

    Object.values(this.config).forEach((config) => {
      totalFields += config.fields.length;
      activeFields += config.fields.filter((f) => f.isActive).length;
      inactiveFields += config.fields.filter((f) => !f.isActive).length;
    });

    return {
      componentCount,
      totalFields,
      activeFields,
      inactiveFields,
    };
  }
}

// Export singleton
export const configurationManager = new ConfigurationManager();

// utils/templateEngine.ts
/**
 * TemplateEngine
 *
 * Renders component HTML with dynamic data.
 * Handles field visibility, label customization, and placeholder replacement.
 */

export class TemplateEngine {
  /**
   * Render a component with configuration and data
   */
  render(
    component: NormalizedComponent,
    data: any = {},
    options: RenderOptions = {}
  ): string {
    const { includeInactive = false } = options;

    try {
      // Handle Graph Component
      if (
        component.alias === "usage_history_graph" ||
        component.type === "Usage History Graph"
      ) {
        return generateGraphHtml(
          component.graphConfig || {
            utilityService: "Gas",
            numberOfMonths: 6,
            showXAxis: true,
            showYAxis: true,
            showValues: true,
          }
        );
      }

      // Pre-process loops (Liquid-like syntax) - Only if data is provided and not empty
      const hasData = data && Object.keys(data).length > 0;
      const processedHtml = hasData
        ? this.processLoops(component.templateHtml, data)
        : component.templateHtml;

      // Parse HTML into DOM
      const parser = new DOMParser();
      const doc = parser.parseFromString(processedHtml, "text/html");

      // Sort fields by order
      const sortedFields = [...component.fields].sort(
        (a, b) => a.order - b.order
      );

      // Process each field
      sortedFields.forEach((field) => {
        if (!field || !field.fieldName || typeof field.fieldName !== "string")
          return;

        // Robust field targeting: try camelCase, snake_case, and original fieldName
        const snakeFieldName = field.fieldName.replace(
          /([A-Z])/g,
          (g) => `_${g[0].toLowerCase()}`
        );
        const camelFieldName = field.fieldName.replace(/_([a-z])/g, (g) =>
          g[1].toUpperCase()
        );

        // Target both standard containers AND table-specific headers/values
        const fieldElements = doc.querySelectorAll(
          `[data-field="${field.fieldName}"], [data-field="${snakeFieldName}"], [data-field="${camelFieldName}"], ` +
            `[data-field-header="${field.fieldName}"], [data-field-header="${snakeFieldName}"], [data-field-header="${camelFieldName}"], ` +
            `[data-field-value="${field.fieldName}"], [data-field-value="${snakeFieldName}"], [data-field-value="${camelFieldName}"]`
        );

        if (fieldElements.length > 0) {
          fieldElements.forEach((el) => {
            const htmlEl = el as HTMLElement;

            if (!field.isActive && !includeInactive) {
              htmlEl.style.display = "none";
            } else {
              htmlEl.style.display = "";

              htmlEl.style.display = "";

              // Handle Labels/Headers
              // Check if the current element IS the label/header, OR if it contains a label child
              const isDirectLabel =
                htmlEl.getAttribute("data-role") === "label" ||
                htmlEl.hasAttribute("data-field-header");
              const labelEl = isDirectLabel
                ? htmlEl
                : htmlEl.querySelector('[data-role="label"]');

              if (labelEl) {
                const labelHtmlEl = labelEl as HTMLElement;
                labelHtmlEl.style.display = field.showLabel ? "" : "none";
                if (field.showLabel) {
                  labelHtmlEl.textContent = field.label;
                }
              }

              // Handle Values
              // Check if the current element IS the value, OR if it contains a value child
              const isDirectValue =
                htmlEl.getAttribute("data-role") === "value" ||
                htmlEl.hasAttribute("data-field-value");
              const valueEl = isDirectValue
                ? htmlEl
                : htmlEl.querySelector('[data-role="value"]');

              if (valueEl) {
                const valueHtmlEl = valueEl as HTMLElement;
                valueHtmlEl.style.display = field.showValue ? "" : "none";

                // SPECIAL: We no longer overwrite value from data
                // We keep what's in the HTML
              }
            }
          });
        } else {
          // Fallback for legacy mapping
          this.hideFieldElements(doc, field.htmlElements);
        }
      });

      // Implement Table Column Reordering
      const tables = doc.querySelectorAll(
        '[data-role="table"], table[data-component]'
      );
      tables.forEach((table) => {
        const rows = table.querySelectorAll("tr");
        rows.forEach((row) => {
          const cells = Array.from(row.children) as HTMLElement[];
          const cellMap = new Map<string, HTMLElement>();

          // Map existing cells by their field keys
          cells.forEach((cell) => {
            const fieldKey =
              cell.getAttribute("data-field-header") ||
              cell.getAttribute("data-field-value");
            if (fieldKey) {
              cellMap.set(fieldKey, cell);
            }
          });

          if (cellMap.size > 0) {
            // Re-append cells in the order specified by field configuration
            sortedFields.forEach((field) => {
              const snake = field.fieldName.replace(
                /([A-Z])/g,
                (g) => `_${g[0].toLowerCase()}`
              );
              const camel = field.fieldName.replace(/_([a-z])/g, (g) =>
                g[1].toUpperCase()
              );

              const target =
                cellMap.get(field.fieldName) ||
                cellMap.get(snake) ||
                cellMap.get(camel);
              if (target) {
                row.appendChild(target);
                // Remove from map to avoid double appending if variations overlap
                cellMap.delete(field.fieldName);
                cellMap.delete(snake);
                cellMap.delete(camel);
              }
            });

            // Append any remaining cells that weren't in the config
            cellMap.forEach((cell) => {
              if (cell.parentElement === row) {
                row.appendChild(cell);
              }
            });
          }
        });
      });

      // Implement Standard Field Container Reordering (for Grid/Flex layouts)
      const fieldContainers = doc.querySelectorAll(
        '[data-role="fields-container"]'
      );
      fieldContainers.forEach((container) => {
        const children = Array.from(container.children) as HTMLElement[];
        const childMap = new Map<string, HTMLElement>();

        // Map existing children by their field keys
        children.forEach((child) => {
          let fieldKey = child.getAttribute("data-field");
          if (fieldKey) {
            childMap.set(fieldKey, child);
          }
        });

        if (childMap.size > 0) {
          // Re-append children in the order specified by field configuration
          sortedFields.forEach((field) => {
            const snake = field.fieldName.replace(
              /([A-Z])/g,
              (g) => `_${g[0].toLowerCase()}`
            );
            const camel = field.fieldName.replace(/_([a-z])/g, (g) =>
              g[1].toUpperCase()
            );

            const target =
              childMap.get(field.fieldName) ||
              childMap.get(snake) ||
              childMap.get(camel);
            if (target) {
              container.appendChild(target);
              childMap.delete(field.fieldName);
              childMap.delete(snake);
              childMap.delete(camel);
            }
          });

          // Append any remaining children
          childMap.forEach((child) => {
            if (child.parentElement === container) {
              container.appendChild(child);
            }
          });
        }
      });

      // Handle custom content for editable components (e.g. Text Box)
      if (component.content) {
        const editableEl = doc.querySelector('[data-role="editable-content"]');
        if (editableEl) {
          editableEl.innerHTML = component.content;

          // Apply styles to editable content if they exist
          if (component.style) {
            const el = editableEl as HTMLElement;
            if (component.style.fontSize)
              el.style.fontSize = `${component.style.fontSize}px`;
            if (component.style.fontWeight)
              el.style.fontWeight = String(component.style.fontWeight);
            if (component.style.color) el.style.color = component.style.color;
            if (component.style.textAlign)
              el.style.textAlign = component.style.textAlign;

            // Handle fontStyle (stored in component.style but type might need it)
            const style = component.style as any;
            if (style.fontStyle) el.style.fontStyle = style.fontStyle;
          }
        }
      }

      // Handle custom image URL (e.g. Logo)
      if (component.imageUrl) {
        const logoImg = doc.querySelector(
          '[data-role="logo-image"]'
        ) as HTMLImageElement;
        if (logoImg) {
          logoImg.src = component.imageUrl;
        }
      }

      let renderedContent = doc.body.innerHTML;

      // Helper to escape regex special characters
      const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      };

      // New: Global placeholder replacement (fallback for direct placeholders like {{customer.name}})
      sortedFields.forEach((field) => {
        if (!field || !field.fieldName) return;

        if (hasData && field.parameter) {
          const value = this.getFieldValue(data, field);
          const snakeParam = field.parameter.replace(
            /([A-Z])/g,
            (g) => `_${g[0].toLowerCase()}`
          );
          const camelParam = field.parameter.replace(/_([a-z])/g, (g) =>
            g[1].toUpperCase()
          );

          // Replace variations of parameter
          const paramVariations = Array.from(
            new Set([field.parameter, snakeParam, camelParam])
          ).filter(Boolean);
          paramVariations.forEach((p) => {
            renderedContent = renderedContent.replace(
              new RegExp(`\\\\{\\\\{${escapeRegExp(p)}\\\\}\\\\}`, "g"),
              value
            );
          });
        }

        // Replace variations of fieldName label only
        const snakeName = field.fieldName.replace(
          /([A-Z])/g,
          (g) => `_${g[0].toLowerCase()}`
        );
        const camelName = field.fieldName.replace(/_([a-z])/g, (g) =>
          g[1].toUpperCase()
        );

        const nameVariations = Array.from(
          new Set([field.fieldName, snakeName, camelName])
        ).filter(Boolean);
        nameVariations.forEach((n) => {
          // We no longer replace the value component of the placeholder
          // renderedContent = renderedContent.replace(
          //   new RegExp(`\\\\{\\\\{${escapeRegExp(n)}\\\\}\\\\}`, "g"),
          //   this.getFieldValue(data, field)
          // );
          renderedContent = renderedContent.replace(
            new RegExp(`\\\\{\\\\{${escapeRegExp(n)}_label\\\\}\\\\}`, "g"),
            field.label
          );
        });
      });

      return renderedContent;
    } catch (error) {
      console.error("❌ Error rendering template:", error);
      return component.originalHtml;
    }
  }

  /**
   * Render preview HTML for iframe
   */
  renderPreview(
    components: NormalizedComponent[],
    data: any = {},
    pageSize: { width: number; height: number } = { width: 794, height: 1123 }
  ): string {
    const sortedComponents = [...components].sort(
      (a, b) => a.zIndex - b.zIndex
    );

    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { 
      margin: 0; 
      padding: 20px; 
      font-family: Arial, sans-serif; 
      background: #f5f5f5; 
    }
    .preview-container { 
      position: relative; 
      width: ${pageSize.width}px; 
      height: ${pageSize.height}px; 
      background: white;
      margin: 0 auto;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }
    .component { 
      position: absolute; 
      box-sizing: border-box; 
      overflow: hidden; 
    }
  </style>
</head>
<body>
  <div class="preview-container">
`;

    sortedComponents.forEach((component) => {
      if (!component.isVisible) return;

      const renderedContent = this.render(component, data);

      const style = `
        left: ${component.x}px;
        top: ${component.y}px;
        width: ${component.width}px;
        height: ${component.height}px;
        z-index: ${component.zIndex};
        background-color: ${component.style.backgroundColor || "transparent"};
        border: ${component.style.borderWidth || 0}px ${
        component.style.borderStyle || "solid"
      } ${component.style.borderColor || "transparent"};
        border-radius: ${component.style.borderRadius || 0}px;
        padding: ${component.style.padding || 0}px;
        font-size: ${component.style.fontSize || 14}px;
        font-weight: ${component.style.fontWeight || "normal"};
        color: ${component.style.color || "#000000"};
        transform: rotate(${component.rotation}deg);
        text-align: ${component.style.textAlign || "left"};
      `;

      html += `<div class="component" style="${style}">${renderedContent}</div>`;
    });

    html += `
  </div>
</body>
</html>
`;

    return html;
  }

  /**
   * Hide field elements (both label and value)
   */
  private hideFieldElements(doc: Document, htmlElements: any) {
    if (!htmlElements) return;

    if (htmlElements.label) {
      this.hideElement(doc, htmlElements.label);
    }
    if (htmlElements.value) {
      this.hideElement(doc, htmlElements.value);
    }
  }

  /**
   * Hide a single element by data-field attribute
   */
  private hideElement(doc: Document, dataFieldValue: string) {
    const elements = doc.querySelectorAll(`[data-field="${dataFieldValue}"]`);

    elements.forEach((element) => {
      // Find appropriate container to hide
      let containerToHide: Element = element;

      // Check if parent is a flex container or field row
      let parent = element.parentElement;
      while (parent && parent !== doc.body) {
        const style = parent.getAttribute("style") || "";

        // If parent is a flex container or has specific structure
        if (
          style.includes("display: flex") ||
          style.includes("display:flex") ||
          parent.classList.contains("field-row")
        ) {
          // Hide the direct child that contains this element
          const children = Array.from(parent.children);
          for (const child of children) {
            if (child.contains(element)) {
              containerToHide = child;
              break;
            }
          }
          break;
        }

        parent = parent.parentElement;
      }

      // Hide by setting display: none
      (containerToHide as HTMLElement).style.display = "none";
    });
  }

  /**
   * Update element content
   */
  private updateElement(
    doc: Document,
    dataFieldValue: string,
    newContent: string
  ) {
    const elements = doc.querySelectorAll(`[data-field="${dataFieldValue}"]`);

    elements.forEach((element) => {
      // For text content
      element.textContent = newContent;
    });
  }

  /**
   * Get field value from data object
   */
  private getFieldValue(data: any, field: any): string {
    const value = this.getNestedValue(data, field.parameter);

    if (value !== null && value !== undefined) {
      return String(value);
    }

    if (field.defaultValue) {
      return field.defaultValue;
    }

    // Return placeholder for missing data
    return field.defaultValue || (field.label ? `[${field.label}]` : "");
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): any {
    if (!path) return null;

    try {
      return path.split(".").reduce((current, key) => {
        if (!current) return undefined;

        // Try direct match
        if (key in current) return current[key];

        // Try variations
        const snakeKey = key.replace(
          /([A-Z])/g,
          (g) => `_${g[0].toLowerCase()}`
        );
        const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

        return current[snakeKey] || current[camelKey];
      }, obj);
    } catch {
      return null;
    }
  }

  /**
   * Process {% for item in items %} loops
   */
  private processLoops(html: string, data: any): string {
    let processedHtml = html;
    const loopRegex =
      /\{%\s*for\s+(\w+)\s+in\s+([\w.]+)\s*%\}([\s\S]*?)\{%\s*endfor\s*%\}/g;

    processedHtml = processedHtml.replace(
      loopRegex,
      (match, itemKey, listKey, content) => {
        const list = this.getNestedValue(data, listKey);

        if (!Array.isArray(list) || list.length === 0) {
          return "";
        }

        return list
          .map((item, index) => {
            let itemHtml = content;

            // Replace {{item.property}} placeholders
            itemHtml = itemHtml.replace(
              new RegExp(`\\{\\{\\s*${itemKey}\\.([\\w.]+)\\s*\\}\\}`, "g"),
              (_, prop) => {
                const value = this.getNestedValue(item, prop);
                return value !== undefined && value !== null
                  ? String(value)
                  : "";
              }
            );

            // Replace {{item}} placeholder (if list is simple primitives)
            itemHtml = itemHtml.replace(
              new RegExp(`\\{\\{\\s*${itemKey}\\s*\\}\\}`, "g"),
              () => {
                return typeof item === "object"
                  ? JSON.stringify(item)
                  : String(item);
              }
            );

            return itemHtml;
          })
          .join("");
      }
    );

    return processedHtml;
  }
}

// Export singleton
export const templateEngine = new TemplateEngine();
